// src/components/PrefixDownloadCard.tsx
import { Box, IconButton, Typography, Stack, CircularProgress, Tooltip } from "@mui/material";
import type { IconButtonProps } from "@mui/material";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import DeleteIcon from "@mui/icons-material/Delete";
import CancelIcon from "@mui/icons-material/Cancel";
import { TOTAL_PAGES, type Prefix } from "../offline/offlineDownload";
import { usePrefixDownloader } from "../offline/usePrefixDownloader";
import { useOnlineStatus } from "../offline/useOnlineStatus";
import { getDownloadStats } from "@/offline/offlineMeta";
import { useEffect, useState } from "react";

export default function PrefixDownloadCard({ prefix }: { prefix: Prefix }) {
  const { status, isDownloaded, progress, error, startDownload, cancelDownload, startDelete } =
    usePrefixDownloader(prefix);
  const online = useOnlineStatus();
  const [storedCount, setStoredCount] = useState<number>(0);

  const totalPages = TOTAL_PAGES[prefix] ?? progress?.totalPages ?? 0;
  const percentages =
    progress && totalPages
      ? Math.round((progress.currentPage / totalPages) * 100)
      : 0;

  useEffect(() => {
    let mounted = true;
    getDownloadStats(prefix).then((stats) => {
      if (mounted && stats?.addressesDownloaded) {
        setStoredCount(stats.addressesDownloaded);
      }
    });
    return () => {
      mounted = false;
    };
  }, [prefix, status]);

  const addressesDownloaded = progress?.addressesDownloaded ?? storedCount;

  const statusLabel = (() => {
    if (status === "downloading" && progress) return `Downloading ${percentages}%`;
    if (status === "queued") return "Queued";
    if (status === "cancelling") return "Cancelling…";
    if (status === "deleting") return "Deleting…";
    if (status === "can_resume") return "Paused";
    if (status === "cancelled") return "Cancelled";
    if (status === "error") return `Error: ${error ?? "Download failed"}`;
    if (isDownloaded) return `${addressesDownloaded || 0} addresses downloaded`;
    return "Not downloaded";
  })();

  const showStatusDetail = status === "starting" || !!error;

  return (
    <Box
      sx={{
        p: 1.5,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        mb: 1.5,
        bgcolor: isDownloaded ? "background.paper" : "grey.50",
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle1" fontWeight={600}>
            {prefix}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {statusLabel}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          {(status === "downloading" || status === "cancelling" || status === "deleting") && (
            <CircularProgress
              size={24}
              variant={status === "downloading" ? "determinate" : "indeterminate"}
              value={status === "downloading" ? percentages : undefined}
            />
          )}
          {renderActionButton({ status, isDownloaded, startDownload, cancelDownload, startDelete, online })}
        </Stack>
      </Stack>
      {/* Status text */}
      {showStatusDetail && (
        <Stack spacing={1} sx={{ mt: 1 }}>
          {status === "starting" && (
            <Typography variant="body2" color="text.secondary">
              Starting download…
            </Typography>
          )}

          {error && (
            <Typography variant="body2" color="error">
              Error: {error}
            </Typography>
          )}
        </Stack>
      )}
    </Box>
  );
}

function renderActionButton({
  status,
  isDownloaded,
  startDownload,
  cancelDownload,
  startDelete,
  online,
}: {
  status: string;
  isDownloaded: boolean;
  startDownload: () => Promise<void>;
  cancelDownload: () => Promise<void>;
  startDelete: () => Promise<void>;
  online: boolean;
}) {
  const common: IconButtonProps = { size: "small", color: "primary" };

  if (status === "downloading") {
    return (
      <Tooltip title="Cancel download">
        <IconButton {...common} color="warning" onClick={cancelDownload}>
          <CancelIcon />
        </IconButton>
      </Tooltip>
    );
  }

  if (status === "can_resume") {
    return (
      <Tooltip title="Resume download">
        <IconButton {...common} onClick={startDownload}>
          <PlayArrowIcon />
        </IconButton>
      </Tooltip>
    );
  }

  if (status === "queued") {
    return (
      <Tooltip title="Cancel queued download">
        <IconButton {...common} color="warning" onClick={cancelDownload}>
          <CancelIcon />
        </IconButton>
      </Tooltip>
    );
  }

  if (isDownloaded && !["downloading", "queued", "cancelling", "deleting"].includes(status)) {
    return (
      <Tooltip title="Delete offline data">
        <IconButton {...common} color="error" onClick={startDelete}>
          <DeleteIcon />
        </IconButton>
      </Tooltip>
    );
  }

  if (status === "cancelling" || status === "deleting") {
    return (
      <Tooltip title="In progress">
        <IconButton {...common} disabled>
          <CloudDownloadIcon />
        </IconButton>
      </Tooltip>
    );
  }

  if (status === "error") {
    return (
      <Tooltip title="Retry download">
        <IconButton {...common} onClick={startDownload}>
          <CloudDownloadIcon />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={online ? "Download" : "Queue for when online"}>
      <IconButton {...common} onClick={startDownload}>
        <CloudDownloadIcon />
      </IconButton>
    </Tooltip>
  );
}
